/**
 * The brand-supplied business figures (VRG-459) — read, write, retract.
 *
 * ## Why react-query and not the redux/saga path the other analytics hooks use
 *
 * `useAnalyticsRoi` / `useAnalyticsTotals` write into single redux slots that the
 * reducer overwrites wholesale, which is why this epic has already paid for two
 * last-writer-wins races (the hero and the ROI cards sharing `analytics.roi`; the
 * totals comparison sharing `analytics.totals`). This resource is read from THREE
 * places at once — the Settings page, an in-card popover, and later the signup form
 * — so a single mutable slot is exactly the wrong shape. A react-query cache keyed
 * on the resource gives every consumer the same object and one invalidation point.
 *
 * ## The catalogue comes from the SERVER, not from a local import
 *
 * `metricInputCatalogue()` is importable here — `@repo/types` is aliased to source
 * in this app's vite config — and calling it locally would still be wrong. The
 * catalogue the form renders must be the one the WRITE endpoint validates against,
 * or a deploy where the API is a version behind renders fields whose values it then
 * rejects. One request carries the fields and this company's answers together, for
 * the same reason the use-case returns them together: a form that knows what to ask
 * but not what is already answered renders empty over a company that filled it in.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query'

import {
  DEFAULT_REPORTING_CURRENCY,
  type IMetricInputDefinition,
  type IMetricInputValue,
  type MetricInputKey,
} from '@repo/types'

import { createApiCall } from 'services'

export type MetricInputsResponse = {
  catalogue: IMetricInputDefinition[]
  values: IMetricInputValue[]
  /**
   * The company's `reporting_currency`, uppercase ISO-4217 (VRG-497).
   *
   * ⚠️ WRITE SIDE ONLY. It is the currency a NEW amount is entered and saved in, and
   * the symbol in an EMPTY field. A stored value is always read in its own
   * `IMetricInputValue.currency` — relabelling a stored amount because the company
   * switched currency restates one currency's number as another's without the number
   * changing. See `metricInputFormat`'s header for all three rules.
   *
   * It rides on this response rather than being fetched separately because three
   * surfaces render this form (settings page, in-card popover, the field itself), and
   * a second request means a window in which a form knows what to ask but not in what
   * unit — which is the same argument that put the catalogue and the values together.
   */
  reporting_currency: string
}

export type SetMetricInputsResponse = MetricInputsResponse & {
  /**
   * Metric ids that now have EVERY input they declare.
   *
   * ⚠️ "Has every input" is NOT "renders a figure". The server puts a metric into
   * `contributions` only when a resolver can produce its number, so a metric can
   * appear here and still be withheld with reason `not_computed` on the next ROI
   * response — `counterfeit_deflection_value` does exactly that today. Any copy
   * built on this field must therefore say what was SAVED, and let the refetched
   * ROI say what now COMPUTES. Promising the second from the first is how a form
   * tells a brand it fixed something it did not.
   */
  unblocked_metric_ids: string[]
}

/** One value on its way to the server. Rates 0–1, currency in MINOR units. */
export type SubmittedMetricInput = {
  key: MetricInputKey
  value: number
  currency?: string
  /** Omitted or false for an in-app save — a human deliberately chose the number. */
  provisional?: boolean
}

export const METRIC_INPUTS_QUERY_KEY = ['analytics', 'metric-inputs'] as const

const ENDPOINT = '/analytics/admin/inputs'

/**
 * The catalogue plus this company's values.
 *
 * Gated server-side on `view_analytics`, NOT on `analytics_input_edit` — see the
 * permission's note in `types/Profile.ts`. So this hook is safe to call from any
 * analytics surface; the WRITE is what a caller must gate.
 */
export const useMetricInputs = (options?: { enabled?: boolean }) => {
  const query = useQuery({
    queryKey: METRIC_INPUTS_QUERY_KEY,
    queryFn: async (): Promise<MetricInputsResponse> => {
      const response = await createApiCall({ method: 'GET', url: ENDPOINT })
      return response.data as MetricInputsResponse
    },
    enabled: options?.enabled !== false,
    // The catalogue changes only on deploy and a company's figures change rarely,
    // so a short window would spend a request per surface visit for nothing. The
    // mutations below invalidate explicitly, which is what actually keeps this fresh.
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  })

  return {
    catalogue: query.data?.catalogue ?? [],
    values: query.data?.values ?? [],
    /**
     * The currency a NEW amount is entered in. `DEFAULT_REPORTING_CURRENCY` while the
     * response is in flight — an empty field has to draw SOME symbol, and the default
     * is the same one the API falls back to for a company that has never set one, so
     * the first paint and the settled paint agree for every company but a
     * non-defaulting one (where the symbol corrects itself on arrival, before anything
     * can be typed into a field that is empty by definition).
     */
    reportingCurrency: query.data?.reporting_currency ?? DEFAULT_REPORTING_CURRENCY,
    /** The response itself, so a caller can tell "not loaded" from "loaded, empty". */
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Upsert one or more inputs. ALL OR NOTHING server-side.
 *
 * Deliberately takes an ARRAY even though both current surfaces save one field at a
 * time. The endpoint's contract is atomic across the payload, and a client helper
 * that only ever sent one would have to be widened — or, worse, looped — the day a
 * surface saves a group. A loop is the failure the atomic contract exists to
 * prevent: the ROI headline moves by an amount corresponding to some of what was
 * typed.
 */
export const useSetMetricInputs = (): UseMutationResult<
  SetMetricInputsResponse,
  unknown,
  SubmittedMetricInput[]
> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (inputs: SubmittedMetricInput[]) => {
      const response = await createApiCall({
        method: 'PUT',
        url: ENDPOINT,
        data: { inputs },
      })
      return response.data as SetMetricInputsResponse
    },
    onSuccess: response => {
      // Seed the cache from the response rather than refetching: the PUT already
      // returns the same shape the GET does (the use-case reads back through the
      // GET's own path precisely so the two cannot drift), so a second round trip
      // would only re-fetch what is already in hand.
      // ⚠️ FIELD BY FIELD, so every field has to be listed — and `reporting_currency`
      // was the one this shape was about to lose (VRG-497). A save would have seeded
      // the cache with it undefined, dropping every money field back to the default
      // symbol until the next full load. Spelled out rather than spread, because the
      // response also carries `unblocked_metric_ids`, which is a fact about THAT save
      // and does not belong in a cache the read hook serves.
      queryClient.setQueryData(METRIC_INPUTS_QUERY_KEY, {
        catalogue: response.catalogue,
        values: response.values,
        reporting_currency: response.reporting_currency,
      })
    },
  })
}

/**
 * Retract one input.
 *
 * Retraction has to exist: without it the only way to undo a wrong figure is to
 * enter a different one, so a metric can never go back to honestly saying "not set"
 * and keeps driving the headline off a number the brand no longer stands behind.
 */
export const useRemoveMetricInput = (): UseMutationResult<
  MetricInputsResponse,
  unknown,
  MetricInputKey
> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (key: MetricInputKey) => {
      const response = await createApiCall({
        method: 'DELETE',
        url: `${ENDPOINT}/${key}`,
      })
      return response.data as MetricInputsResponse
    },
    onSuccess: response => {
      queryClient.setQueryData(METRIC_INPUTS_QUERY_KEY, response)
    },
  })
}
